import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import './Users.css';
import { formatDateDDMMYY } from '../utils/date';

const MyAbsences = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ userId: '', type: 'other', startDate: '', endDate: '', reason: '' });
  const [message, setMessage] = useState({ type: '', text: '' });

  const normalizeArray = useCallback((value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.absences)) return value.absences;
    return [];
  }, []);

  const normalizeDate = useCallback((value) => (value ? String(value).slice(0, 10) : '-'), []);
  const displayDate = useCallback((value) => formatDateDDMMYY(value), []);

  const load = useCallback(async () => {
    try {
      const [profile, data] = await Promise.all([api.get('/auth/profile'), api.get('/absences/me')]);
      const uid = profile?.userId || profile?.id || '';
      setForm((prev) => ({ ...prev, userId: uid }));
      setRecords(
        normalizeArray(data).map((r) => ({
          ...r,
          startDate: normalizeDate(r.startDate),
          endDate: normalizeDate(r.endDate),
        })),
      );
    } catch (error) {
      console.error('Error loading my absences:', error);
    } finally {
      setLoading(false);
    }
  }, [normalizeArray, normalizeDate]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ type: '', text: '' }), 2500);
    return () => clearTimeout(timer);
  }, [message]);

  const submitRequest = async (e) => {
    e.preventDefault();
    try {
      if (!form.userId || !form.startDate || !form.endDate || !form.reason.trim()) {
        setMessage({ type: 'error', text: 'All fields are required.' });
        return;
      }
      if (new Date(form.startDate) > new Date(form.endDate)) {
        setMessage({ type: 'error', text: 'End date cannot be before start date.' });
        return;
      }

      await api.post('/absences', {
        userId: Number(form.userId),
        type: form.type,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason.trim(),
      });

      setForm((prev) => ({ ...prev, type: 'other', startDate: '', endDate: '', reason: '' }));
      await load();
      setMessage({ type: 'success', text: 'Leave request submitted successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to submit leave request.' });
    }
  };

  if (loading) return <div className="page-loading">Loading my leaves...</div>;

  return (
    <div className="users-page">
      <div className="page-header"><h1>My Leaves</h1></div>

      {message.text && (
        <div className={`remote-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="users-table-container request-leave-box" style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px 0', color: 'var(--text)' }}>Request Leave</h3>
        <form onSubmit={submitRequest}>
          <div className="form-row">
            <div className="form-group">
              <label>Leave Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required>
                <option value="sick">Sick</option>
                <option value="vacation">Vacation</option>
                <option value="personal">Personal</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Reason</label>
              <input
                type="text"
                placeholder="e.g. Family emergency"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" value={form.endDate} min={form.startDate || undefined} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
            </div>
          </div>

          <div className="modal-actions" style={{ marginTop: 4 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setForm((prev) => ({ ...prev, type: 'other', startDate: '', endDate: '', reason: '' }))}
            >
              Clear
            </button>
            <button className="btn btn-primary" type="submit">Submit Request</button>
          </div>
        </form>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr><th>ID</th><th>Type</th><th>Period</th><th>Status</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {records.length ? records.map((r) => (
              <tr key={r.id}>
                <td>#{r.id}</td>
                <td>
                  <span className={`leave-type-chip ${(r.type || 'other').toLowerCase()}`}>{r.type}</span>
                </td>
                <td>
                  <div className="leave-period-cell">
                    <span>{displayDate(r.startDate)}</span>
                    <span className="period-arrow">→</span>
                    <span>{displayDate(r.endDate)}</span>
                  </div>
                </td>
                <td>
                  <span className={`leave-status-chip ${String(r.status || '').toLowerCase() || 'pending'}`}>{r.status}</span>
                </td>
                <td>
                  <span className="leave-reason-text">{r.reason || '-'}</span>
                </td>
              </tr>
            )) : <tr><td colSpan="5" className="empty-row">No leave records</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyAbsences;
